'use client'

import React from 'react'
import { Box, Container, Typography, Button } from '@mui/material'

import {
    CustomerSupportIcon,
    FastBookingIcon,
    SecurePaymentIcon,
    AirlineIcon
} from '@/src/constants/homepagePromoIcons'
import { TranslationFunction } from '@/src/types/translations'

interface MobileAppPromotionProps {
    t: TranslationFunction<'homePage.promotion'>
    tFooter: TranslationFunction<'footer'>
    isRtl: boolean
}

const MobileAppPromotion: React.FC<MobileAppPromotionProps> = ({t, tFooter, isRtl}) => {
    
    return (
        <Box
            sx={{
                bgcolor: '#f5f7fa',
                mt: { xs: 4, sm: 6 },
                mb: { xs: 4, sm: 6 },
                py: { xs: 4, sm: 6, md: 8 },
                display: { xs: 'none', sm: 'none', md: 'block' }
            }}
        >
            <Container maxWidth="lg">
                {/* Mobile App Promotional Banner */}
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, rgb(67, 129, 205) 0%, rgb(25, 77, 145) 100%)',
                        borderRadius: 2,
                        position: 'relative',
                        overflow: 'hidden',
                        py: { xs: 3, sm: 4, md: 5 },
                        px: { xs: 3, sm: 4, md: 6 }
                    }}
                >
                    <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                minHeight: 180
                            }}
                        >
                            {/* Text Content - Centered */}
                            <Box
                                sx={{
                                    width: '100%',
                                    mb: 4
                                }}
                            >
                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: 'white',
                                        fontWeight: 'bold',
                                        mb: 2,
                                        fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {t('title')}
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: 'white',
                                        mb: 4,
                                        fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                                        opacity: 0.9
                                    }}
                                >
                                    {t('subtitle')}
                                </Typography>

                                {/* App Download Buttons */}
                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                    {/* Google Play Button */}
                                    <Button
                                        href="https://play.google.com/store/apps/details?id=com.flyin.bookings"
                                        target="_blank"
                                        className="android"
                                        sx={{
                                            backgroundColor: 'white',
                                            color: 'black',
                                            px: 4,
                                            py: 2,
                                            borderRadius: 1,
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            fontSize: '0.9rem',
                                            minWidth: 180,
                                            height: 60,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                            position: 'relative',
                                            '&:hover': {
                                                backgroundColor: '#f8f9fa'
                                            },
                                            '&::before': {
                                                content: '""',
                                                display: 'inline-block',
                                                height: '44px',
                                                width: '100%',
                                                background:
                                                    'url(https://me.flyin.com/common/themes/v2/img/appBtnSprite.png) -144px -63px / 320px no-repeat',
                                                position: 'absolute',
                                                top: '50%',
                                                left: isRtl ? '45%' : '55%',
                                                transform: 'translate(-50%, -50%)',
                                                zIndex: 1
                                            }
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                zIndex: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '100%',
                                                height: '100%'
                                            }}
                                        >
                                            {/* Sprite icon contains all text, no extra text needed */}
                                        </Box>
                                    </Button>

                                    {/* App Store Button */}
                                    <Button
                                        href="https://itunes.apple.com/us/app/flyin.com-tyran-w-fnadq/id1106268318"
                                        target="_blank"
                                        className="ios"
                                        sx={{
                                            backgroundColor: 'white',
                                            color: 'black',
                                            px: 4,
                                            py: 2,
                                            borderRadius: 1,
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            fontSize: '0.9rem',
                                            minWidth: 180,
                                            height: 60,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                            position: 'relative',
                                            '&:hover': {
                                                backgroundColor: '#f8f9fa'
                                            },
                                            '&::before': {
                                                content: '""',
                                                display: 'inline-block',
                                                height: '44px',
                                                width: '100%',
                                                background:
                                                    'url(https://me.flyin.com/common/themes/v2/img/appBtnSprite.png) -135px -9px / 320px no-repeat',
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                zIndex: 1
                                            }
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                zIndex: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '100%',
                                                height: '100%'
                                            }}
                                        >
                                            {/* Sprite icon contains all text, no extra text needed */}
                                        </Box>
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </Container>
                </Box>
            </Container>

            {/* Feature Highlights */}
            <Container maxWidth="lg" sx={{ mt: { xs: 6, sm: 8 } }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        overflow: 'hidden',
                        height: { xs: 'auto', sm: '164px' },
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}
                >
                    {/* Feature 1: 24/7 Customer Support */}
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: { xs: 3, sm: 2 },
                            position: 'relative',
                            borderBottom: { xs: '1px solid', sm: 'none' },
                            borderColor: { xs: 'divider', sm: 'transparent' },
                            minHeight: { xs: '120px', sm: 'auto' },
                            '&::after': {
                                content: { xs: 'none', sm: '""' },
                                position: 'absolute',
                                right: 0,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                height: '60%',
                                width: '1px',
                                bgcolor: 'rgba(0, 0, 0, 0.12)'
                            }
                        }}
                    >
                        <CustomerSupportIcon />

                        <Typography
                            sx={{
                                fontSize: '16px',
                                fontWeight: 600,
                                color: 'text.primary',
                                textAlign: 'center'
                            }}
                        >
                            {tFooter('customerSupport247')}
                        </Typography>
                    </Box>

                    {/* Feature 2: Easy & Fast Booking */}
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: { xs: 3, sm: 2 },
                            position: 'relative',
                            borderBottom: { xs: '1px solid', sm: 'none' },
                            borderColor: { xs: 'divider', sm: 'transparent' },
                            minHeight: { xs: '120px', sm: 'auto' },
                            '&::after': {
                                content: { xs: 'none', sm: '""' },
                                position: 'absolute',
                                right: 0,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                height: '60%',
                                width: '1px',
                                bgcolor: 'rgba(0, 0, 0, 0.12)'
                            }
                        }}
                    >
                        <FastBookingIcon />

                        <Typography
                            sx={{
                                fontSize: '16px',
                                fontWeight: 600,
                                color: 'text.primary',
                                textAlign: 'center'
                            }}
                        >
                            {tFooter('easyFastBooking')}
                        </Typography>
                    </Box>

                    {/* Feature 3: Secure Payment Options */}
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: { xs: 3, sm: 2 },
                            position: 'relative',
                            borderBottom: { xs: '1px solid', sm: 'none' },
                            borderColor: { xs: 'divider', sm: 'transparent' },
                            minHeight: { xs: '120px', sm: 'auto' },
                            '&::after': {
                                content: { xs: 'none', sm: '""' },
                                position: 'absolute',
                                right: 0,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                height: '60%',
                                width: '1px',
                                bgcolor: 'rgba(0, 0, 0, 0.12)'
                            }
                        }}
                    >
                        <SecurePaymentIcon />

                        <Typography
                            sx={{
                                fontSize: '16px',
                                fontWeight: 600,
                                color: 'text.primary',
                                textAlign: 'center'
                            }}
                        >
                            {tFooter('securePaymentOptions')}
                        </Typography>
                    </Box>

                    {/* Feature 4: 30+ Airline options */}
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: { xs: 3, sm: 2 },
                            minHeight: { xs: '120px', sm: 'auto' }
                        }}
                    >
                        <AirlineIcon />

                        <Typography
                            sx={{
                                fontSize: '16px',
                                fontWeight: 600,
                                color: 'text.primary',
                                textAlign: 'center'
                            }}
                        >
                            {tFooter('airlineOptions')}
                        </Typography>
                    </Box>
                </Box>
            </Container>
        </Box>
    )
}

export { MobileAppPromotion }
export type { MobileAppPromotionProps }
